import SharedDialog from '../../../../shared/shared-dialog';
import { handleErrors } from '../../../../utils/errors';
import {
  UserKeys,
  getCalendarAcls,
  updateCalendarAcls,
} from '../../../../api/users-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, CalendarAclResource } from '../../../../../types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import ChangeVisibilityDialog from './change-visibility-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { emailRegex } from '../../../../utils/regular-expressions';
import UserAvatar from '../../../../shared/user-avatar';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Clear from '@mui/icons-material/Clear';
import Domain from '@mui/icons-material/Domain';
import Lock from '@mui/icons-material/Lock';
import Public from '@mui/icons-material/Public';

interface CalendarShareDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedCalendar: Calendar;
  setSelectedCalendar: Dispatch<SetStateAction<Calendar | null>>;
  primaryDomain: string;
  calendarRoles: string[];
  changeLogRoles: string[];
  isAllUsers: boolean;
}

export default function CalendarShareDialog({
  open,
  handleClose,
  userEmail,
  selectedCalendar,
  setSelectedCalendar,
  primaryDomain,
  calendarRoles,
  changeLogRoles,
  isAllUsers,
}: CalendarShareDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [inputValue, setInputValue] = useState('');
  const [openVisibilty, setOpenVisibility] = useState(false);
  const [selectedVisibility, setSelectedVisibility] = useState('private');
  const [selectedRole, setSelectedRole] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const [emailError, setEmailError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: [UserKeys.CalendarAcls, userEmail, selectedCalendar.id],
    queryFn: () => getCalendarAcls(userEmail, selectedCalendar),
    meta: {
      errorMessage:
        'There was an issue getting calendar permissions. Please refresh and try again.',
    },
  });

  const { mutate, isPending: isSharing } = useMutation({
    mutationFn: (data: { aclResources: CalendarAclResource[] }) =>
      updateCalendarAcls(userEmail, selectedCalendar, data.aclResources),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar share permissions updated successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.CalendarAcls, userEmail, selectedCalendar.id],
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue sharing this calendar. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.update_calendar_shares
          : analytics.user_management.user_details.calendars
              .update_calendar_shares,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  // set selected visibility based on data
  useEffect(() => {
    if (data && data.find((acl) => acl.scope.type === 'default_public')) {
      const acl = data.find((acl) => acl.scope.type === 'default_public');
      setSelectedVisibility('default_public');
      setSelectedRole(acl?.role || '');
    } else if (data && data.find((acl) => acl.scope.type === 'domain')) {
      const domain = data.find((acl) => acl.scope.type === 'domain');
      setSelectedVisibility('domain');
      setSelectedRole(domain?.role || '');
    } else {
      setSelectedVisibility('private');
      setSelectedRole('freeBusyReader');
    }
  }, [data, setSelectedVisibility]);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    getValues,
  } = useForm({
    values: {
      aclResources:
        data?.filter(
          (acl) => acl.scope.type === 'user' || acl.scope.type === 'group'
        ) || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'aclResources',
  });
  function mutateData() {
    const formIds = getValues('aclResources').map((acl) => acl.id);

    if (!data) return;

    // update toBeDeleted flag
    const updatedData = data.map((acl) => {
      if (
        !formIds.includes(acl.id) &&
        !acl.id?.includes('domain:') &&
        !acl.id?.includes('default')
      ) {
        return {
          ...acl,
          toBeDeleted: true,
        };
      } else {
        const updatedAcl =
          getValues('aclResources').find((a) => a.id === acl.id) || acl;
        return updatedAcl;
      }
    });

    // add any new users
    const newUsers = getValues('aclResources').filter(
      (acl) => !data.find((a) => a.id === acl.id)
    );
    updatedData.push(...newUsers);

    // remove current user from list
    const filteredData = updatedData.filter(
      (acl) => acl.scope.value !== userEmail
    );

    // Only include changes
    const changedData = filteredData.filter((acl) => {
      const originalAcl = data.find((original) => original.id === acl.id);
      return (
        !originalAcl || JSON.stringify(originalAcl) !== JSON.stringify(acl)
      );
    });
    return changedData;
  }

  const onSubmit: SubmitHandler<{
    aclResources: CalendarAclResource[];
  }> = (formData) => {
    const initialAcls =
      data?.filter(
        (acl) => acl.scope.type === 'user' || acl.scope.type === 'group'
      ) || [];
    const hasRemovedShare = initialAcls.some(
      (initialAcl) =>
        !formData.aclResources.some(
          (currentAcl) => currentAcl.id === initialAcl.id
        )
    );
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.AddShareToCalendar.value
      ) ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.RemoveCalendarsShares.value
      ) &&
        hasRemovedShare)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const filteredData = mutateData()!;
      mutate({ aclResources: filteredData });
    }
  };

  let listItem = <></>;

  if (selectedVisibility === 'private') {
    listItem = (
      <ListItem dense>
        <ListItemAvatar>
          <Avatar>
            <Lock />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary="Private"
          secondary="Only people listed below can access"
        />
      </ListItem>
    );
  } else if (selectedVisibility === 'default_public') {
    const acl = data?.find((acl) => acl.scope.type === 'default_public');
    listItem = (
      <ListItem dense>
        <ListItemAvatar>
          <Avatar>
            <Public />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={`Public: (${
            acl?.role === 'freeBusyReader'
              ? 'Free/Busy Viewer'
              : acl?.role === 'reader'
              ? 'Viewer'
              : acl?.role === 'writer'
              ? 'Editor'
              : 'Owner'
          })`}
          secondary="Anyone on the Internet can find and access. No sign-in required."
        />
      </ListItem>
    );
  } else if (selectedVisibility === 'domain') {
    const domain = data?.find((acl) => acl.scope.type === 'domain');

    listItem = (
      <ListItem dense>
        <ListItemAvatar>
          <Avatar>
            <Domain />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={`Domain: (${
            domain?.role === 'freeBusyReader'
              ? 'Free/Busy Viewer'
              : domain?.role === 'reader'
              ? 'Viewer'
              : domain?.role === 'writer'
              ? 'Editor'
              : 'Owner'
          })`}
          secondary={`Anyone at ${
            domain?.id ? domain?.id.split(':')[1] : 'domain'
          } can find and access`}
        />
      </ListItem>
    );
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setSelectedCalendar(null);
      }}
      title={`Share "${selectedCalendar?.summary}"`}
      actingAs={selectedCalendar.accessRole !== 'owner' ? userEmail : undefined}
      dialogContent={
        <Stack>
          <Typography>Visibility:</Typography>
          {data ? listItem : <Skeleton variant="rounded" height={60} />}
          {isAbleToSeeUIElement(
            calendarRoles,
            CalendarRoles.EditCalendarsShares.value
          ) ? (
            <Button
              sx={{
                textTransform: 'none',
                color: 'inherit',
                textDecoration: 'underline',
                p: 0,
                ml: 'auto',
              }}
              disabled={selectedCalendar.accessRole !== 'owner'}
              onClick={() => setOpenVisibility(true)}
            >
              Change
            </Button>
          ) : null}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ px: 1 }}>
            <Stack flexDirection="row" gap={1} sx={{ mb: 1 }}>
              <Box sx={{ width: '100%' }}>
                <SharedAutocomplete
                  autoFocus
                  sources={['users', 'groups']}
                  autocompleteValue={inputValue}
                  setAutoCompleteValue={setInputValue}
                  disabled={selectedCalendar.accessRole !== 'owner'}
                  freeSolo
                  onSelection={(value) => {
                    if (!value) return;
                    if (!emailRegex.test(value)) {
                      setEmailError('Please enter a valid email address.');
                      return;
                    }

                    if (fields.find((acl) => acl.scope.value === value)) {
                      setEmailError('This email is already added.');
                      return;
                    }

                    if (value === '') {
                      setEmailError('Email cannot be empty.');
                      return;
                    }

                    append({
                      id: `user:${value}`,
                      canActOn: true,
                      toBeDeleted: false,
                      role: 'reader',
                      scope: {
                        type: 'user',
                        value: value,
                      },
                    });

                    setInputValue('');
                    setEmailError(''); // Clear error message after successful addition
                  }}
                />
              </Box>
            </Stack>
            {emailError && (
              <Typography
                sx={{ color: 'error.main', fontSize: '0.875rem', mt: 0.5 }}
              >
                {emailError}
              </Typography>
            )}

            <Typography>Who has access:</Typography>
            {isLoading ? (
              <Stack gap={1}>
                <Skeleton variant="rounded" height={60} />
              </Stack>
            ) : (
              <List sx={{ maxHeight: 160, overflowY: 'auto' }}>
                {fields.map((acl, index) => {
                  return (
                    <ListItem
                      key={acl.id}
                      dense
                      secondaryAction={
                        <Stack flexDirection="row" gap={1} alignItems="center">
                          <Controller
                            name={`aclResources.${index}.role`}
                            control={control}
                            rules={{
                              required: 'Calendar Name is required.',
                            }}
                            render={({ field }) => (
                              <>
                                <TextField
                                  {...field}
                                  inputProps={{
                                    id: 'role',
                                  }}
                                  InputLabelProps={{
                                    htmlFor: 'role',
                                  }}
                                  select
                                  defaultValue={acl.role}
                                  disabled={
                                    acl.scope.value === userEmail ||
                                    selectedCalendar.accessRole !== 'owner'
                                  }
                                  label="Role"
                                  size="small"
                                  sx={{ width: 140 }}
                                >
                                  <MenuItem key="writer" value="writer">
                                    Editor
                                  </MenuItem>
                                  <MenuItem
                                    key="freeBusyReader"
                                    value="freeBusyReader"
                                  >
                                    Free/Busy Viewer
                                  </MenuItem>
                                  <MenuItem key="owner" value="owner">
                                    Owner
                                  </MenuItem>

                                  <MenuItem key="reader" value="reader">
                                    Viewer
                                  </MenuItem>
                                  <MenuItem
                                    key="writerWithoutPrivateAccess"
                                    value="writerWithoutPrivateAccess"
                                  >
                                    Make changes (see private events as
                                    free/busy)
                                  </MenuItem>
                                </TextField>
                                {acl.scope.value !== userEmail ? (
                                  <Tooltip arrow title="Remove">
                                    <IconButton
                                      size="small"
                                      disabled={
                                        selectedCalendar.accessRole !== 'owner'
                                      }
                                      onClick={() => remove(index)}
                                    >
                                      <Clear />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Box sx={{ width: 34, height: 34 }}></Box>
                                )}
                              </>
                            )}
                          />
                        </Stack>
                      }
                    >
                      <ListItemAvatar>
                        <UserAvatar userEmail={acl.scope.value || ''} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={acl.scope.value}
                        primaryTypographyProps={{
                          maxWidth: 200,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const filteredData = mutateData()!;
              mutate({ aclResources: filteredData });
            }}
            mutateLoading={isSharing}
          />
          {isAbleToSeeUIElement(
            calendarRoles,
            CalendarRoles.EditCalendarsShares.value
          ) ? (
            <ChangeVisibilityDialog
              open={openVisibilty}
              handleClose={() => setOpenVisibility(false)}
              selectedVisibility={
                selectedVisibility as
                  | 'group'
                  | 'user'
                  | 'private'
                  | 'default_public'
                  | 'domain'
              }
              selectedRole={selectedRole}
              userEmail={userEmail}
              selectedCalendar={selectedCalendar}
              primaryDomain={primaryDomain}
              changeLogRoles={changeLogRoles}
              aclId={
                selectedVisibility === 'default_public'
                  ? data?.find((acl) => acl.scope.type === 'default_public')?.id
                  : data?.find((acl) => acl.scope.type === 'domain')?.id
              }
              isAllUsers={isAllUsers}
            />
          ) : null}
        </Stack>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        loading: isSharing,
        disabled: isLoading || isSharing || !isDirty,
      }}
    />
  );
}
